import React from 'react'
import { useMediaQuery } from 'react-responsive'
import { css } from '@emotion/react'
import { GatsbyImage } from 'gatsby-plugin-image'

import { PostFrontmatterType, PostItemType } from '~/types/post.types'

import { imageMapper } from '~/utils/queryUtils'
import { useCommonStore } from '~/states/commonStore'

import { Label } from '.'

import { ReactiveWrapper } from '~/styles/image'
import { ellipsis, multiEllipsis } from '~/styles/text'
import typography from '~/styles/typography'
import { darkTheme, lightTheme } from '~/styles/mode'
import palette from '~/styles/palette'

interface Props extends PostFrontmatterType, Pick<PostItemType['node'], 'excerpt' | 'timeToRead'> {
  onlyMW?: boolean
}

const PostItem: React.FC<Props> = ({
  title,
  date,
  thumbnail,
  tags,
  timeToRead,
  excerpt,
  onlyMW,
}) => {
  const { isLightMode } = useCommonStore()

  const isMobile = useMediaQuery({ query: '(max-width: 620px)' })
  const isMobileCss = onlyMW || isMobile
  const RootCss = isMobileCss ? ColumnCss(isLightMode) : RowCss(isLightMode)

  return (
    <ReactiveWrapper height={isMobileCss ? '93%' : '31%'}>
      <div css={RootCss}>
        <GatsbyImage className="thumbnail" image={imageMapper(thumbnail)} alt={title} />

        <div className="text-wrapper">
          <h4>{title}</h4>

          {!isMobileCss && (
            <ul className="tag-list">
              {tags.map(tag => (
                <Label key={tag}>{tag}</Label>
              ))}
            </ul>
          )}

          <span className="date">{`${date} - ${timeToRead} min read`}</span>
          <p>{excerpt}</p>

          {isMobileCss && (
            <ul className="tag-list">
              {tags.map(tag => (
                <Label key={tag}>{tag}</Label>
              ))}
            </ul>
          )}
        </div>
      </div>
    </ReactiveWrapper>
  )
}

const CommonCss = (isLightMode: boolean) => css`
  display: flex;
  box-shadow: 0 0 3px rgb(${isLightMode ? '0 0 0' : '255 255 255'} / 20%);
  border-radius: 20px 20px 20px 0;
  cursor: pointer;

  .thumbnail {
    height: 100%;
  }

  .text-wrapper {
    height: 100%;
    display: flex;
    flex-direction: column;

    .date {
      ${typography.tag1}
      color: #999;
      font-family: unset;
    }

    .tag-list {
      display: flex;
      column-gap: 5px;

      @media (max-width: 620px) {
        margin: auto 0 0 auto;
      }
    }

    h4 {
      ${ellipsis}
      margin-bottom: 3px;
      color: ${isLightMode ? lightTheme.defaultText : darkTheme.defaultText};
    }

    p {
      ${typography.body2}
      color: ${isLightMode ? lightTheme.darkText : palette.bodyGray};
    }
  }
`

// 포스트 페이지
const RowCss = (isLightMode: boolean) => css`
  ${CommonCss(isLightMode)}

  flex-direction: row;
  row-gap: 9px;

  .thumbnail {
    width: 30%;
    height: 100%;
    border-radius: 20px 0 0 0;

    img {
      border-radius: 20px 0 0 0;
    }
  }

  .text-wrapper {
    width: 70%;
    padding: 15px;
    row-gap: 10px;

    p {
      ${multiEllipsis(3)}
      font-size: 14.5px;
      line-height: 27px;

      @media (max-width: 700px) {
        ${multiEllipsis(2)}
      }
    }
  }
`

// 슬라이더
const ColumnCss = (isLightMode: boolean) => css`
  ${CommonCss(isLightMode)}

  margin-right: 10px;
  margin-bottom: 50px;

  flex-direction: column;
  column-gap: 2px;

  .thumbnail {
    height: 150%;
    border-radius: 20px 20px 0 0;

    img {
      border-radius: 20px 20px 0 0;
    }
  }

  .text-wrapper {
    ${typography.body3}
    font-family: nanumsquare;
    padding: 12px 10px;
    row-gap: 7px;

    p {
      ${multiEllipsis(2)}
      ${typography.body3}
      height: 43px;
      margin-bottom: 4.5px;
    }
  }
`

export default PostItem
